import React, { useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Breadcrumbs, Link } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DynamicForm from '../components/forms/DynamicForm';
import { servicesCatalog } from '../config/formsConfig';
import { AuthContext } from '../context/AuthContext';
import api from '../api/api';

const font = "'Montserrat', sans-serif";

const MATCHING_MESSAGES = {
  locating: 'Obteniendo tu ubicación...',
  matching: 'Buscando prestadores disponibles...',
};

function ServiceRequestPage({ serviceKey: propKey }) {
  const { serviceKey: paramKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const serviceKey   = propKey || paramKey;
  const catalogEntry = servicesCatalog.find(s => s.slug === serviceKey);
  const serviceTitle = location.state?.serviceTitle || catalogEntry?.name || serviceKey;

  const [matchingStatus,   setMatchingStatus]   = React.useState(null);
  const [verificationData, setVerificationData] = React.useState(null);
  const [checkingVerif,    setCheckingVerif]    = React.useState(true);

  // Verificar estado de verificaciones al montar
  React.useEffect(() => {
    window.scrollTo(0, 0);
    api.get('/users/me')
      .then(res => {
        const d = res.data.user || res.data;
        setVerificationData({
          email_verified: !!d.email_verified,
          phone_verified: !!d.phone_verified,
        });
      })
      .catch(() => setVerificationData({ email_verified: true, phone_verified: true }))
      .finally(() => setCheckingVerif(false));
  }, [serviceKey]);

  const getCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ lat: null, lng: null });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        ()    => resolve({ lat: null, lng: null }),
        { timeout: 5000 }
      );
    });

  // Si /matching/run falla, la solicitud ya quedó creada: el cliente se entera
  // acá y puede reintentar la búsqueda sobre la misma solicitud. Antes se
  // tragaba con un console.warn y la tarjeta quedaba "Creada" sin ir a ningún
  // lado (26-10-2026).
  const [solicitudId, setSolicitudId] = React.useState(null);

  const buscarPrestadores = async (serviceRequestId) => {
    setMatchingStatus('locating');
    const { lat, lng } = await getCoords();
    setMatchingStatus('matching');
    try {
      await api.post(`/matching/run/${serviceRequestId}`, { client_lat: lat, client_lng: lng });
    } catch (err) {
      console.warn('[ServiceRequestPage] Matching no pudo ejecutarse:', err.message);
      setMatchingStatus('error');
      return;
    }
    navigate('/mis-solicitudes', { state: { fromSubmit: true, serviceTitle } });
  };

  const handleSubmitSuccess = async (serviceRequestId) => {
    if (!serviceRequestId) {
      navigate('/mis-solicitudes', { state: { fromSubmit: true, serviceTitle } });
      return;
    }
    setSolicitudId(serviceRequestId);
    await buscarPrestadores(serviceRequestId);
  };

  // Loading verificaciones
  if (checkingVerif) {
    return (
      <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font }}>
        <CircularProgress size={28} sx={{ color: '#163244' }} />
      </Box>
    );
  }

  // Guard — email no verificado
  if (verificationData && !verificationData.email_verified) {
    return (
      <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', px: { xs: 2, sm: 3, md: 4 }, py: 3, fontFamily: font }}>
        <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6 }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: '18px', border: '1px solid rgba(0,0,0,0.08)', p: 4, textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(231,164,50,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, mx: 'auto', mb: 2 }}>
              ✉️
            </Box>
            <Typography sx={{ fontFamily: font, fontSize: 18, fontWeight: 800, color: '#163244', mb: 1 }}>
              Verificá tu email primero
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: 13, color: '#7a93a4', mb: 3, lineHeight: 1.6 }}>
              Para solicitar servicios necesitás verificar tu dirección de email. Es un proceso rápido — te enviamos un código de 6 dígitos.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box
                component="button"
                onClick={() => navigate('/perfil')}
                sx={{ px: 3, py: 1.5, borderRadius: '50px', border: 'none', bgcolor: '#163244', color: '#fff', fontFamily: font, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                Ir a mi perfil →
              </Box>
              <Box
                component="button"
                onClick={() => navigate(-1)}
                sx={{ px: 3, py: 1.5, borderRadius: '50px', border: '1.5px solid rgba(22,50,68,0.2)', bgcolor: 'transparent', color: '#163244', fontFamily: font, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Volver
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Guard — teléfono no verificado
  // Nota: phone_verified se activa cuando se implemente la verificación por WhatsApp/SMS (Bloque 7)
  // Por ahora solo se bloquea si phone_verified es explícitamente false (no NULL)
  if (verificationData && verificationData.phone_verified === false) {
    return (
      <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', px: { xs: 2, sm: 3, md: 4 }, py: 3, fontFamily: font }}>
        <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6 }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: '18px', border: '1px solid rgba(0,0,0,0.08)', p: 4, textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(231,164,50,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, mx: 'auto', mb: 2 }}>
              📱
            </Box>
            <Typography sx={{ fontFamily: font, fontSize: 18, fontWeight: 800, color: '#163244', mb: 1 }}>
              Verificá tu teléfono primero
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: 13, color: '#7a93a4', mb: 3, lineHeight: 1.6 }}>
              Para solicitar servicios también necesitás verificar tu número de teléfono. Te enviamos un código por SMS.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box
                component="button"
                onClick={() => navigate('/perfil')}
                sx={{ px: 3, py: 1.5, borderRadius: '50px', border: 'none', bgcolor: '#163244', color: '#fff', fontFamily: font, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                Ir a mi perfil →
              </Box>
              <Box
                component="button"
                onClick={() => navigate(-1)}
                sx={{ px: 3, py: 1.5, borderRadius: '50px', border: '1.5px solid rgba(22,50,68,0.2)', bgcolor: 'transparent', color: '#163244', fontFamily: font, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Volver
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', px: { xs: 2, sm: 3, md: 4 }, py: 3, fontFamily: font }}>

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          onClick={() => navigate('/?scroll=servicios')}
          underline="hover"
          sx={{ color: '#163244', fontFamily: font, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          Servicios
        </Link>
        <Typography sx={{ fontFamily: font, fontSize: 12, color: '#7a93a4' }}>{serviceTitle}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          component="button"
          onClick={() => navigate('/?scroll=servicios')}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.8,
            background: '#fff', border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '10px', px: 1.5, py: 1,
            fontSize: 12, fontWeight: 600, color: '#163244',
            cursor: 'pointer', fontFamily: font,
            transition: 'border-color 0.15s, background 0.15s',
            flexShrink: 0,
            '&:hover': { borderColor: '#E7A432', background: 'rgba(231,164,50,0.04)' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 14 }} />
          Volver
        </Box>

        <Box sx={{ width: 40, height: 40, bgcolor: '#163244', borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <LocationOnIcon sx={{ color: '#E7A432', fontSize: 20 }} />
        </Box>

        <Box>
          <Typography sx={{ fontFamily: font, fontSize: 18, fontWeight: 800, color: '#163244', lineHeight: 1.2 }}>
            {serviceTitle}
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: 12, color: '#7a93a4', mt: 0.3 }}>
            Completá el formulario y te conectamos con los mejores prestadores
          </Typography>
        </Box>
      </Box>

      {/* La búsqueda falló: la solicitud quedó guardada. Se reemplaza el
          formulario para que no la vuelva a mandar por duplicado. */}
      {matchingStatus === 'error' && (
        <Box sx={{ maxWidth: 560, mx: 'auto', mt: 2, bgcolor: '#fff', borderRadius: '18px', border: '1px solid rgba(192,57,43,0.25)', p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: font, fontSize: 17, fontWeight: 800, color: '#163244', mb: 1 }}>
            No pudimos buscar prestadores para tu solicitud en este momento
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: 13, color: '#7a93a4', mb: 3, lineHeight: 1.6 }}>
            Tu solicitud quedó guardada. Podés volver a intentar la búsqueda ahora, o hacerlo más tarde desde Mis solicitudes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box component="button" onClick={() => buscarPrestadores(solicitudId)}
              sx={{ px: 3, py: 1.5, borderRadius: '50px', border: 'none', bgcolor: '#163244', color: '#fff', fontFamily: font, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Reintentar búsqueda
            </Box>
            <Box component="button" onClick={() => navigate('/mis-solicitudes')}
              sx={{ px: 3, py: 1.5, borderRadius: '50px', border: '1.5px solid rgba(22,50,68,0.2)', bgcolor: 'transparent', color: '#163244', fontFamily: font, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Ir a Mis solicitudes
            </Box>
          </Box>
        </Box>
      )}

      {/* Feedback matching */}
      {matchingStatus && matchingStatus !== 'error' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, borderRadius: '12px', backgroundColor: 'rgba(22,50,68,0.06)', border: '1px solid rgba(22,50,68,0.15)' }}>
          <CircularProgress size={18} sx={{ color: '#163244', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: font, fontSize: 13, color: '#163244', fontWeight: 600 }}>
            {MATCHING_MESSAGES[matchingStatus]}
          </Typography>
        </Box>
      )}

      {/* Layout: form + sidebar */}
      <Box sx={{ display: matchingStatus === 'error' ? 'none' : 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 264px' }, gap: 2.5, alignItems: 'stretch' }}>

        <Box sx={{ bgcolor: '#fff', borderRadius: '18px', border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden', p: { xs: 2, sm: 3 } }}>
          <DynamicForm
            serviceKey={serviceKey}
            serviceTitle={serviceTitle}
            onSubmitSuccess={handleSubmitSuccess}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          <Box sx={{ bgcolor: '#163244', borderRadius: '16px', p: 2.5, position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: `radial-gradient(circle at 85% 15%, rgba(231,164,50,0.1) 0%, transparent 55%), repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.015) 40px, rgba(255,255,255,0.015) 41px)` }} />
            <Box sx={{ position: 'relative' }}>
              <Typography sx={{ fontFamily: font, fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 1.2, mb: 1 }}>
                Tu solicitud
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: 17, fontWeight: 800, color: '#fff', mb: 0.5 }}>
                {serviceTitle}
              </Typography>
              <Box sx={{ display: 'inline-block', bgcolor: 'rgba(231,164,50,0.15)', color: '#E7A432', border: '1px solid rgba(231,164,50,0.3)', borderRadius: '50px', px: 1.5, py: 0.3, fontSize: 10, fontWeight: 700, mb: 2, fontFamily: font }}>
                Solicitud de servicio
              </Box>
              {[
                { label: 'Estado',    value: 'Pendiente de envío' },
                { label: 'Matching',  value: 'Automático al enviar' },
                { label: 'Respuesta', value: 'En minutos' },
              ].map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography sx={{ fontFamily: font, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{label}</Typography>
                  <Typography sx={{ fontFamily: font, fontSize: 12, color: '#fff', fontWeight: 600 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ bgcolor: '#fff', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', p: 2.5, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.8 }}>
              <Box sx={{ width: 3, height: 14, bgcolor: '#E7A432', borderRadius: '2px', flexShrink: 0 }} />
              <Typography sx={{ fontFamily: font, fontSize: 11, fontWeight: 700, color: '#163244', textTransform: 'uppercase', letterSpacing: 1 }}>
                ¿Qué pasa después?
              </Typography>
            </Box>
            {[
              { n: '1', title: 'Buscamos prestadores', desc: 'Analizamos disponibilidad y zona para encontrar los mejores.' },
              { n: '2', title: 'Recibís propuestas',   desc: 'Los prestadores te envían presupuestos para comparar.' },
              { n: '3', title: 'Confirmás y listo',    desc: 'Elegís el que más te convenga y coordinan directo.' },
            ].map(({ n, title, desc }) => (
              <Box key={n} sx={{ display: 'flex', gap: 1.5, mb: 1.5, '&:last-child': { mb: 0 } }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'rgba(231,164,50,0.12)', color: '#b8820d', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2, fontFamily: font }}>{n}</Box>
                <Box>
                  <Typography sx={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: '#163244', mb: 0.3 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: font, fontSize: 12, color: '#7a93a4', lineHeight: 1.5 }}>{desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

        </Box>
      </Box>
    </Box>
  );
}

export default ServiceRequestPage;
export { ServiceRequestPage };
